import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { CalendarClock, Download, Inbox, Link2, Mail, MailOpen, RefreshCw } from 'lucide-react';
import {
  apiClient,
  type ApiError,
  type CaseListItem,
  type Intimacao,
  type IntimacoesLidoFilter,
  type IntimacoesListResponse,
  type MonitoredTerm,
  type TaskCategory,
} from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { categoryColorClasses } from '@/components/office-config/term-category-colors';
import { TaskListInline } from '@/components/tasks/task-list-inline';
import { TaskFormModal } from '@/components/tasks/task-form-modal';
import { useSession } from '@/lib/use-session';
import { isHtmlContent, sanitizeDjenHtml, stripHtmlToText } from '@/lib/html-content';

interface OabFilterItem {
  id: string;
  numero: string;
  uf: string;
}

/**
 * Retorna a data avancada em `n` dias uteis (pula sab/dom).
 * Mesma logica do scheduler backend (J07).
 */
function addBusinessDays(date: Date, n: number): Date {
  const result = new Date(date);
  let added = 0;
  while (added < n) {
    result.setDate(result.getDate() + 1);
    const dow = result.getDay();
    if (dow !== 0 && dow !== 6) added++;
  }
  return result;
}

/** Preposicoes que ficam em minusculo no Title Case PT-BR. */
const PREPOSICOES = new Set(['de', 'da', 'do', 'das', 'dos', 'e', 'a', 'o']);

/**
 * Converte uma string em CAIXA ALTA para Title Case PT-BR.
 * Preposicoes curtas ficam em minusculo (exceto a primeira palavra).
 * Trunca em `maxLen` chars adicionando "…" se necessario.
 */
function toTitleCase(str: string, maxLen = 40): string {
  const result = str
    .toLowerCase()
    .split(/\s+/)
    .map((word, i) =>
      i > 0 && PREPOSICOES.has(word)
        ? word
        : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(' ');
  if (result.length <= maxLen) return result;
  return result.slice(0, maxLen - 1).trimEnd() + '…';
}

/** Remove acentos para comparacao insensivel a diacritico. */
function stripAccents(value: string): string {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/**
 * Monta o titulo do item da lista de intimacoes.
 * Formato: "{Ativo} x {Passivo} · {numero}" (omite tipo se for Intimacao/Comunicacao)
 * ou degradacoes graceiosas se faltar polo.
 */
function buildIntimacaoTitle(item: Intimacao): string {
  const tipo = item.tipoComunicacao ?? 'Comunicação';
  const numero = item.numeroProcessoMascara ?? item.numeroProcesso;
  const parsed = item.parsed as { poloAtivo?: string | null; poloPassivo?: string | null } | undefined;
  const ativo = parsed?.poloAtivo ? toTitleCase(parsed.poloAtivo) : null;
  const passivo = parsed?.poloPassivo ? toTitleCase(parsed.poloPassivo) : null;

  // Omite prefixo de tipo se for redundante (Intimacao ou Comunicacao).
  // Compara sem acento: o tribunal pode mandar o tipo acentuado ou nao.
  const shouldOmitTipo = ['intimacao', 'comunicacao'].includes(stripAccents(tipo).toLowerCase());
  const tipoPrefix = shouldOmitTipo ? '' : `${tipo} · `;

  if (ativo && passivo) return `${tipoPrefix}${ativo} x ${passivo} · ${numero}`;
  if (ativo) return `${tipoPrefix}${ativo} · ${numero}`;
  if (passivo) return `${tipoPrefix}${passivo} · ${numero}`;
  return `${numero}`;
}

/**
 * J01 v1 — Inbox de intimacoes do DJEN.
 *
 * Layout: sidebar de filtros + lista paginada + painel lateral com detalhe
 * da intimacao selecionada. Em mobile o painel ocupa a tela inteira (drawer).
 */
export default function IntimacoesPage() {
  const [data, setData] = useState<IntimacoesListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Intimacao | null>(null);

  const [lidoEm, setLidoEm] = useState<IntimacoesLidoFilter>('null');
  const [oabId, setOabId] = useState<string | undefined>(undefined);
  const [tribunal, setTribunal] = useState<string>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [oabsFilter, setOabsFilter] = useState<OabFilterItem[]>([]);
  const [tribunalsAvailable, setTribunalsAvailable] = useState<string[]>([]);
  const [monitoredTerms, setMonitoredTerms] = useState<MonitoredTerm[]>([]);
  const [termFilter, setTermFilter] = useState<string | undefined>(undefined);

  // Mapa id -> termo, usado para renderizar chips na inbox.
  const termsById = useMemo(() => {
    const m = new Map<string, MonitoredTerm>();
    for (const t of monitoredTerms) m.set(t.id, t);
    return m;
  }, [monitoredTerms]);

  useEffect(() => {
    apiClient
      .intimacoesListOabsForFilter()
      .then((rows) => setOabsFilter(rows))
      .catch(() => setOabsFilter([]));
    apiClient
      .monitoredTermsList('true')
      .then((rows) => setMonitoredTerms(rows))
      .catch(() => setMonitoredTerms([]));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiClient
      .intimacoesList({
        lidoEm,
        oabId,
        tribunal: tribunal || undefined,
        search: search.trim() || undefined,
        page,
        pageSize: 25,
      })
      .then((res) => {
        if (cancelled) return;
        setData(res);
        const tribunals = Array.from(
          new Set(res.items.map((i) => i.siglaTribunal)),
        ).sort();
        setTribunalsAvailable((prev) =>
          prev.length === 0 ? tribunals : prev,
        );
      })
      .catch((e: ApiError) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [lidoEm, oabId, tribunal, search, page]);

  function refresh() {
    setPage(1);
    setLidoEm((prev) => prev);
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[220px_minmax(0,1fr)_minmax(0,1.2fr)]">
      <aside className="space-y-4">
        <div data-tour="intimacoes-filtro-status">
        <FilterSection title="Status">
          <FilterButton
            active={lidoEm === 'null'}
            onClick={() => {
              setLidoEm('null');
              setPage(1);
            }}
          >
            <Mail className="h-4 w-4" /> Não lidas
          </FilterButton>
          <FilterButton
            active={lidoEm === 'all'}
            onClick={() => {
              setLidoEm('all');
              setPage(1);
            }}
          >
            <Inbox className="h-4 w-4" /> Todas
          </FilterButton>
          <FilterButton
            active={lidoEm === 'notnull'}
            onClick={() => {
              setLidoEm('notnull');
              setPage(1);
            }}
          >
            <MailOpen className="h-4 w-4" /> Lidas
          </FilterButton>
        </FilterSection>
        </div>

        {oabsFilter.length > 0 && (
          <FilterSection title="Monitorado">
            <FilterButton
              active={!oabId}
              onClick={() => {
                setOabId(undefined);
                setPage(1);
              }}
            >
              Todos
            </FilterButton>
            {oabsFilter.map((o) => (
              <FilterButton
                key={o.id}
                active={oabId === o.id}
                onClick={() => {
                  setOabId(o.id);
                  setPage(1);
                }}
              >
                {o.uf ? `${o.numero}/${o.uf}` : o.numero}
              </FilterButton>
            ))}
          </FilterSection>
        )}

        {monitoredTerms.length > 0 && (
          <FilterSection title="Termo monitorado">
            <FilterButton
              active={!termFilter}
              onClick={() => {
                setTermFilter(undefined);
                setPage(1);
              }}
            >
              Todos
            </FilterButton>
            {monitoredTerms.map((t) => (
              <FilterButton
                key={t.id}
                active={termFilter === t.id}
                onClick={() => {
                  setTermFilter(t.id);
                  setPage(1);
                }}
              >
                <span
                  className={cn(
                    'inline-block h-2 w-2 rounded-full',
                    categoryColorClasses(t.category).split(' ')[0] ??
                      'bg-zinc-500/30',
                  )}
                  aria-hidden
                />
                <span className="truncate">{t.term}</span>
              </FilterButton>
            ))}
          </FilterSection>
        )}

        {tribunalsAvailable.length > 0 && (
          <FilterSection title="Tribunal">
            <FilterButton
              active={!tribunal}
              onClick={() => {
                setTribunal('');
                setPage(1);
              }}
            >
              Todos
            </FilterButton>
            {tribunalsAvailable.map((t) => (
              <FilterButton
                key={t}
                active={tribunal === t}
                onClick={() => {
                  setTribunal(t);
                  setPage(1);
                }}
              >
                {t}
              </FilterButton>
            ))}
          </FilterSection>
        )}
      </aside>

      <section className="min-w-0">
        <div data-tour="intimacoes-busca" className="mb-3 flex items-center justify-between gap-3">
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Buscar no texto..."
            className="h-9 w-full max-w-sm rounded-md border border-[var(--border)] bg-transparent px-3 text-sm text-[var(--fg)] placeholder:text-[var(--fg-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
          />
          <Button variant="outline" size="sm" onClick={refresh}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>

        <div data-tour="intimacoes-lista" className="overflow-hidden rounded-lg border border-[var(--card-border)] bg-[var(--card-bg)] shadow-[var(--shadow-card)]">
          {loading ? (
            <p className="px-4 py-8 text-center text-sm text-[var(--fg-muted)]">
              Carregando...
            </p>
          ) : error ? (
            <p className="px-4 py-8 text-center text-sm text-red-500">
              {error}
            </p>
          ) : !data || data.items.length === 0 ? (
            <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
              <Inbox className="h-10 w-10 text-[var(--fg-muted)] opacity-40" />
              <p className="text-sm text-[var(--fg-muted)]">
                Nenhuma intimação encontrada com esses filtros.
              </p>
            </div>
          ) : (
            <ul>
              {data.items
                .filter(
                  (it) =>
                    !termFilter ||
                    (it.monitoredTermIds ?? []).includes(termFilter),
                )
                .map((item) => (
                <li
                  key={item.id}
                  className={cn(
                    'cursor-pointer border-b border-[var(--border)] px-4 py-3 transition hover:bg-[color-mix(in_oklch,var(--fg)_4%,transparent)]',
                    selected?.id === item.id &&
                      'bg-[color-mix(in_oklch,var(--accent)_8%,transparent)]',
                    !item.lidoEm && 'font-medium',
                  )}
                  onClick={() => setSelected(item)}
                >
                  <div className="flex items-center justify-between gap-2 text-xs text-[var(--fg-muted)]">
                    <span>{item.siglaTribunal}</span>
                    <span>
                      {new Date(item.dataDisponibilizacao).toLocaleDateString(
                        'pt-BR',
                      )}
                    </span>
                  </div>
                  <p className="mt-1 truncate text-sm text-[var(--fg)]">
                    {buildIntimacaoTitle(item)}
                  </p>
                  {item.monitoredTermIds &&
                    item.monitoredTermIds.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {item.monitoredTermIds.slice(0, 4).map((tid) => {
                          const t = termsById.get(tid);
                          if (!t) return null;
                          return (
                            <span
                              key={tid}
                              className={cn(
                                'rounded-full px-2 py-0.5 text-[10px] font-medium',
                                categoryColorClasses(t.category),
                              )}
                              title={t.category ?? undefined}
                            >
                              {t.term}
                            </span>
                          );
                        })}
                        {item.monitoredTermIds.length > 4 && (
                          <span className="rounded-full bg-zinc-500/15 px-2 py-0.5 text-[10px] font-medium text-zinc-400">
                            +{item.monitoredTermIds.length - 4}
                          </span>
                        )}
                      </div>
                    )}
                  <p className="mt-1 line-clamp-2 text-xs text-[var(--fg-muted)]">
                    {stripHtmlToText(item.texto)}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </div>

        {data && data.totalPages > 1 && (
          <div className="mt-3 flex items-center justify-between text-xs text-[var(--fg-muted)]">
            <span>
              Página {data.page} de {data.totalPages} · {data.total} no total
            </span>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={data.page <= 1}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setPage((p) => Math.min(data.totalPages, p + 1))
                }
                disabled={data.page >= data.totalPages}
              >
                Próxima
              </Button>
            </div>
          </div>
        )}
      </section>

      <IntimacaoDetail
        intimacao={selected}
        onUpdate={(updated) => {
          setSelected(updated);
          setData((prev) =>
            prev
              ? {
                  ...prev,
                  items: prev.items.map((i) =>
                    i.id === updated.id ? updated : i,
                  ),
                }
              : prev,
          );
        }}
      />
    </div>
  );
}

function FilterSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <p className="mb-1 px-2 text-xs font-semibold uppercase tracking-wider text-[var(--fg-muted)]">
        {title}
      </p>
      <div className="flex flex-col gap-0.5">{children}</div>
    </div>
  );
}

function FilterButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 rounded-md px-3 py-1.5 text-left text-sm transition',
        'text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)] hover:text-[var(--fg)]',
        active &&
          'bg-[color-mix(in_oklch,var(--accent)_12%,transparent)] text-[var(--fg)]',
      )}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Modal de prazo manual
// ---------------------------------------------------------------------------

function PrazoManualModal({
  taskId,
  currentFatalDate,
  onClose,
  onSaved,
}: {
  taskId: string;
  currentFatalDate: string | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [value, setValue] = useState(currentFatalDate?.slice(0, 10) ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  async function handleSave() {
    if (!value) return;
    setSaving(true);
    setError(null);
    try {
      await apiClient.tasksUpdate(taskId, { fatalDate: value });
      onSaved();
    } catch (e) {
      setError((e as ApiError).message ?? 'Erro ao salvar prazo.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-sm rounded-xl border border-[var(--border)] bg-[var(--surface)] p-5 shadow-xl">
        <h3 className="mb-3 text-sm font-semibold text-[var(--fg)]">
          Definir prazo fatal manualmente
        </h3>
        <input
          ref={inputRef}
          type="date"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className="mb-3 h-9 w-full rounded-md border border-[var(--border)] bg-transparent px-3 text-sm text-[var(--fg)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
        />
        {error && (
          <p className="mb-2 text-xs text-red-400">{error}</p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={saving}>
            Cancelar
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving || !value}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Painel de detalhe
// ---------------------------------------------------------------------------

function IntimacaoDetail({
  intimacao,
  onUpdate,
}: {
  intimacao: Intimacao | null;
  onUpdate: (i: Intimacao) => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const [cases, setCases] = useState<CaseListItem[]>([]);
  const [caseSearch, setCaseSearch] = useState('');
  const [linking, setLinking] = useState(false);
  const [marking, setMarking] = useState(false);
  const [linkPickerOpen, setLinkPickerOpen] = useState(false);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [taskCategories, setTaskCategories] = useState<TaskCategory[]>([]);

  // Prazo manual — taskId buscado via lista de tasks
  const [prazoTaskId, setPrazoTaskId] = useState<string | null>(null);
  const [prazoTaskFatalDate, setPrazoTaskFatalDate] = useState<string | null>(null);
  const [showPrazoModal, setShowPrazoModal] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(t);
  }, [toast]);

  // Busca a task vinculada para obter o id e fatal_date
  useEffect(() => {
    if (!intimacao) {
      setPrazoTaskId(null);
      setPrazoTaskFatalDate(null);
      return;
    }
    apiClient
      .intimacoesListTasks(intimacao.id)
      .then((tasks) => {
        const first = tasks[0] ?? null;
        setPrazoTaskId(first?.id ?? null);
        setPrazoTaskFatalDate(first?.fatalDate ?? null);
      })
      .catch(() => {
        setPrazoTaskId(null);
        setPrazoTaskFatalDate(null);
      });
  }, [intimacao?.id]);

  useEffect(() => {
    if (!linkPickerOpen) return;
    apiClient
      .casesList({ search: caseSearch || undefined, pageSize: 10 })
      .then((res) => setCases(res.items))
      .catch(() => setCases([]));
  }, [linkPickerOpen, caseSearch]);

  useEffect(() => {
    apiClient
      .taskCategoriesList({ active: 'true' })
      .then(setTaskCategories)
      .catch(() => setTaskCategories([]));
  }, []);

  const certidaoUrl = useMemo(
    () => (intimacao ? apiClient.intimacoesCertidaoUrl(intimacao.id) : null),
    [intimacao],
  );

  // Data de entrega = dataDisponibilizacao + 1 dia util (CPC art. 1003 §1)
  const dataEntrega = useMemo(() => {
    if (!intimacao) return null;
    try {
      // dataDisponibilizacao vem como 'YYYY-MM-DD' (date sem timezone)
      const [y, m, d] = intimacao.dataDisponibilizacao.split('-').map(Number);
      const base = new Date(y, (m ?? 1) - 1, d ?? 1);
      return addBusinessDays(base, 1);
    } catch {
      return null;
    }
  }, [intimacao?.dataDisponibilizacao]);

  if (!intimacao) {
    return (
      <aside
        data-tour="intimacoes-detalhe"
        className="hidden rounded-lg border border-dashed border-[var(--card-border)] bg-[var(--card-bg)] p-6 text-center text-sm text-[var(--fg-muted)] shadow-[var(--shadow-card)] lg:block"
      >
        Selecione uma intimação para ver os detalhes.
      </aside>
    );
  }

  async function handleMarkRead() {
    if (!intimacao || intimacao.lidoEm) return;
    setMarking(true);
    try {
      const updated = await apiClient.intimacoesMarkAsRead(intimacao.id);
      onUpdate(updated);
    } catch (e) {
      window.alert((e as ApiError).message);
    } finally {
      setMarking(false);
    }
  }

  async function handleLink(caseId: string | null) {
    if (!intimacao) return;
    setLinking(true);
    try {
      const updated = await apiClient.intimacoesLinkCase(intimacao.id, caseId);
      onUpdate(updated);
      setLinkPickerOpen(false);
    } catch (e) {
      window.alert((e as ApiError).message);
    } finally {
      setLinking(false);
    }
  }

  // taskGeneratedAt indica se o scheduler ja gerou task para esta intimacao
  const taskJaGerada = !!intimacao.taskGeneratedAt;

  return (
    <aside
      data-tour="intimacoes-detalhe"
      className="overflow-hidden rounded-lg border border-[var(--card-border)] bg-[var(--card-bg)] shadow-[var(--shadow-card)]"
    >
      <header className="space-y-1 border-b border-[var(--border)] px-5 py-4">
        <div className="flex items-center justify-between text-xs text-[var(--fg-muted)]">
          <span>{intimacao.siglaTribunal}</span>
          <div className="flex items-center gap-3">
            <span>
              Disponibilizado em{' '}
              {new Date(intimacao.dataDisponibilizacao).toLocaleDateString('pt-BR')}
            </span>
            {dataEntrega && (
              <span className="font-medium text-[var(--fg)]">
                Entrega:{' '}
                {dataEntrega.toLocaleDateString('pt-BR')}
              </span>
            )}
          </div>
        </div>
        <h2 className="text-base font-semibold text-[var(--fg)]">
          {intimacao.tipoComunicacao ?? 'Comunicação'}
        </h2>
        <p className="text-xs text-[var(--fg-muted)]">
          Processo:{' '}
          {intimacao.caseId ? (
            <Link
              to={`/escritorio/processos/${intimacao.caseId}`}
              className="text-[var(--accent)] hover:underline cursor-pointer"
            >
              {intimacao.numeroProcessoMascara ?? intimacao.numeroProcesso}
            </Link>
          ) : (
            <span>{intimacao.numeroProcessoMascara ?? intimacao.numeroProcesso}</span>
          )}
        </p>
        {intimacao.nomeOrgao && (
          <p className="text-xs text-[var(--fg-muted)]">{intimacao.nomeOrgao}</p>
        )}
        {/* Nome pesquisado (sujeito monitorado que captou esta intimacao) */}
        {intimacao.subjectDisplayName && (
          <p className="text-xs text-[var(--fg-muted)]">
            Pesquisado:{' '}
            <span className="font-medium text-[var(--fg)]">
              {intimacao.subjectDisplayName}
            </span>
            {intimacao.oabNumero && (
              <span>
                {' '}
                (OAB {intimacao.oabNumero}
                {intimacao.oabUf ? `/${intimacao.oabUf}` : ''})
              </span>
            )}
          </p>
        )}
      </header>

      <div className="space-y-3 px-5 py-4">
        <div className="flex flex-wrap gap-2">
          <Button
            variant={intimacao.lidoEm ? 'outline' : 'default'}
            size="sm"
            onClick={handleMarkRead}
            disabled={marking || !!intimacao.lidoEm}
          >
            <MailOpen className="mr-1.5 h-4 w-4" />
            {intimacao.lidoEm ? 'Marcada como lida' : 'Marcar como lida'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setLinkPickerOpen((v) => !v)}
            disabled={linking}
          >
            <Link2 className="mr-1.5 h-4 w-4" />
            {intimacao.caseId ? 'Trocar processo' : 'Vincular a processo'}
          </Button>
          {certidaoUrl && (
            <a
              href={certidaoUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[var(--border)] px-3 text-xs font-medium text-[var(--fg)] hover:bg-[color-mix(in_oklch,var(--fg)_8%,transparent)]"
            >
              <Download className="h-4 w-4" />
              Baixar certidão (PDF)
            </a>
          )}
          {/* Botao de prazo manual */}
          <Button
            variant="outline"
            size="sm"
            disabled={!taskJaGerada || !prazoTaskId}
            title={
              !taskJaGerada
                ? 'Aguardando geração da task pelo scheduler'
                : !prazoTaskId
                  ? 'Carregando task...'
                  : 'Editar prazo fatal da task gerada'
            }
            onClick={() => {
              if (prazoTaskId) setShowPrazoModal(true);
            }}
          >
            <CalendarClock className="mr-1.5 h-4 w-4" />
            Definir prazo manualmente
          </Button>
        </div>

        {intimacao.caseNumeroCnj && (
          <p className="rounded-md bg-[color-mix(in_oklch,var(--accent)_8%,transparent)] px-3 py-2 text-xs text-[var(--fg)]">
            Vinculada ao processo CNJ {intimacao.caseNumeroCnj}
          </p>
        )}

        {linkPickerOpen && (
          <div className="rounded-md border border-[var(--border)] p-3">
            <input
              autoFocus
              value={caseSearch}
              onChange={(e) => setCaseSearch(e.target.value)}
              placeholder="Buscar processo por CNJ ou parte..."
              className="mb-2 h-8 w-full rounded-md border border-[var(--border)] bg-transparent px-2 text-xs focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
            />
            <ul className="max-h-48 space-y-1 overflow-y-auto">
              {intimacao.caseId && (
                <li>
                  <button
                    type="button"
                    onClick={() => handleLink(null)}
                    className="w-full rounded-md px-2 py-1 text-left text-xs text-red-400 hover:bg-[color-mix(in_oklch,var(--fg)_6%,transparent)]"
                  >
                    Desvincular do processo atual
                  </button>
                </li>
              )}
              {cases.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => handleLink(c.id)}
                    className="w-full rounded-md px-2 py-1 text-left text-xs text-[var(--fg-muted)] hover:bg-[color-mix(in_oklch,var(--fg)_6%,transparent)] hover:text-[var(--fg)]"
                  >
                    {c.numeroCnj ?? '(sem CNJ)'} —{' '}
                    {c.mainPartyNames || c.actionTypeName || c.status}
                  </button>
                </li>
              ))}
              {cases.length === 0 && (
                <li className="px-2 py-1 text-xs text-[var(--fg-muted)]">
                  Nenhum processo encontrado.
                </li>
              )}
            </ul>
          </div>
        )}

        {intimacao.matches && intimacao.matches.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {intimacao.matches.map((m) => (
              <span
                key={m.termId}
                className={cn(
                  'rounded-full px-2 py-0.5 text-[10px] font-medium',
                  categoryColorClasses(m.category),
                )}
                title={m.category ?? undefined}
              >
                {m.term}
              </span>
            ))}
          </div>
        )}

        <div className="rounded-md border border-[var(--border)] bg-[color-mix(in_oklch,var(--fg)_2%,transparent)] p-3">
          {isHtmlContent(intimacao.texto) ? (
            <div
              className="text-xs text-[var(--fg)] [&_a]:text-[var(--accent)] [&_a]:underline [&_h1]:text-sm [&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold [&_p]:my-1 [&_section]:block [&_strong]:font-semibold [&_table]:w-full [&_td]:border [&_td]:border-[var(--border)] [&_td]:px-1 [&_th]:border [&_th]:border-[var(--border)] [&_th]:px-1 [&_th]:font-semibold [&_[align=center]]:text-center"
              dangerouslySetInnerHTML={{ __html: sanitizeDjenHtml(intimacao.texto) }}
            />
          ) : (
            <pre className="whitespace-pre-wrap text-xs text-[var(--fg)]">
              {renderHighlightedText(
                intimacao.texto,
                intimacao.matches ?? [],
              )}
            </pre>
          )}
        </div>

        {intimacao.linkInteiroTeor && (
          <a
            href={intimacao.linkInteiroTeor}
            target="_blank"
            rel="noreferrer"
            className="block text-xs text-[var(--accent)] underline"
          >
            Ver inteiro teor no tribunal
          </a>
        )}

        <div className="space-y-2 border-t border-[var(--border)] pt-3">
          <p className="text-xs text-[var(--fg-muted)]">
            Tarefas geradas automaticamente a partir desta intimação
            aparecem aqui. Você também pode criar manualmente com o
            botão abaixo.
          </p>
          <TaskListInline
            query={() => apiClient.intimacoesListTasks(intimacao.id)}
            queryKey={['intimacoes', intimacao.id, 'tasks']}
            onNewTask={() => setShowCreateTask(true)}
            title="Tarefas geradas"
            emptyMessage="Sem tarefas vinculadas a esta intimação ainda."
            maxRows={5}
          />
        </div>
      </div>

      {showCreateTask && (
        <TaskFormModal
          categories={taskCategories}
          onClose={() => setShowCreateTask(false)}
          onCreated={() => {
            setShowCreateTask(false);
            void queryClient.invalidateQueries({
              queryKey: ['intimacoes', intimacao.id, 'tasks'],
            });
            void queryClient.invalidateQueries({ queryKey: ['tasks'] });
          }}
          currentUserId={session.user?.id ?? ''}
          tenantId={session.tenantId ?? ''}
          intimacaoId={intimacao.id}
          caseId={intimacao.caseId ?? null}
          initialFatalDate={
            intimacao.prazoDataLimite
              ? intimacao.prazoDataLimite.slice(0, 10)
              : null
          }
        />
      )}

      {showPrazoModal && prazoTaskId && (
        <PrazoManualModal
          taskId={prazoTaskId}
          currentFatalDate={prazoTaskFatalDate}
          onClose={() => setShowPrazoModal(false)}
          onSaved={() => {
            setShowPrazoModal(false);
            setToast('Prazo atualizado com sucesso.');
            void queryClient.invalidateQueries({
              queryKey: ['intimacoes', intimacao.id, 'tasks'],
            });
            void queryClient.invalidateQueries({ queryKey: ['tasks'] });
          }}
        />
      )}

      {toast && (
        <div
          className="popover-menu fixed bottom-4 left-1/2 -translate-x-1/2 rounded-md px-4 py-2 text-sm text-[var(--fg)]"
          role="status"
        >
          {toast}
        </div>
      )}
    </aside>
  );
}

/**
 * Renderiza o texto com spans destacando cada `match.text` na posicao
 * informada. Sobrepoe-se ao layout do <pre>: usamos spans inline com
 * background semi-transparente da paleta da categoria.
 *
 * Se a posicao do banco estiver fora do range (texto editado), cai no
 * indexOf para tentar recuperar o offset. Se ainda assim nao encontrar,
 * o termo nao recebe destaque mas a tag continua aparecendo nos chips
 * acima — degradacao aceitavel.
 */
function renderHighlightedText(
  text: string,
  matches: ReadonlyArray<{
    termId: string;
    term: string;
    category: string | null;
    position: number | null;
    text: string | null;
  }>,
): React.ReactNode[] {
  if (matches.length === 0) return [text];

  interface Span {
    start: number;
    end: number;
    category: string | null;
    key: string;
  }

  const spans: Span[] = [];
  for (const m of matches) {
    const slice = m.text ?? m.term;
    let start = m.position ?? -1;
    if (
      start < 0 ||
      start >= text.length ||
      text.slice(start, start + slice.length) !== slice
    ) {
      start = text.indexOf(slice);
    }
    if (start < 0) continue;
    spans.push({
      start,
      end: start + slice.length,
      category: m.category,
      key: `${m.termId}-${start}`,
    });
  }
  spans.sort((a, b) => a.start - b.start);

  const out: React.ReactNode[] = [];
  let cursor = 0;
  for (const s of spans) {
    if (s.start < cursor) continue; // overlap: skip
    if (s.start > cursor) out.push(text.slice(cursor, s.start));
    out.push(
      <mark
        key={s.key}
        className={
          'rounded-sm px-0.5 ' + categoryColorClasses(s.category)
        }
      >
        {text.slice(s.start, s.end)}
      </mark>,
    );
    cursor = s.end;
  }
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
}
